import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Bold, Italic, List, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { containsHebrew } from '../utils/textUtils';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  required?: boolean;
}

const RichTextEditor = forwardRef<any, RichTextEditorProps>(({
  value,
  onChange,
  placeholder = "Start typing...",
  className = '',
  minHeight = "150px",
  required = false
}, ref) => {
  const { language } = useLanguage();
  const [editorDir, setEditorDir] = useState<'rtl' | 'ltr'>(language === 'he' ? 'rtl' : 'ltr');
  const editorRef = useRef<HTMLDivElement>(null);
  const [isHebrew, setIsHebrew] = useState(false);

  // Update direction based on content or language
  useEffect(() => {
    // Set initial direction based on language and/or content
    const initialDir = containsHebrew(value) || language === 'he' ? 'rtl' : 'ltr';
    setEditorDir(initialDir);
  }, [language, value]);

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current) {
      const hasHebrewText = containsHebrew(value);
      setIsHebrew(hasHebrewText);
      
      // Set the content
      if (!editorRef.current.innerHTML || editorRef.current.innerHTML === placeholder) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value, placeholder]);

  const formatDoc = (command: string, value: string | null = null) => {
    document.execCommand(command, false, value);
    
    // Focus back on the editor after formatting
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  // Handle input changes
  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const content = e.currentTarget.innerHTML;
    onChange(content);
    
    // Check if content contains Hebrew characters
    const hasHebrewText = containsHebrew(content);
    setIsHebrew(hasHebrewText);
    setEditorDir(hasHebrewText ? 'rtl' : 'ltr');
  };

  // Handle paste to make links clickable but preserve plain text
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    
    // Get plain text from clipboard
    const text = e.clipboardData.getData('text/plain');
    
    // Check if the text is a URL
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    if (urlRegex.test(text)) {
      // If it's a URL, create a clickable link
      const linkHtml = text.replace(urlRegex, '<a href="$&" target="_blank" rel="noopener noreferrer">$&</a>');
      document.execCommand('insertHTML', false, linkHtml);
    } else {
      // Otherwise insert as plain text
      document.execCommand('insertText', false, text);
    }
  };

  // Handle placeholder visibility
  const handleFocus = () => {
    if (editorRef.current?.innerHTML === placeholder) {
      editorRef.current.innerHTML = '';
    }
  };

  const handleBlur = () => {
    if (editorRef.current && !editorRef.current.innerHTML.trim()) {
      editorRef.current.innerHTML = placeholder;
    }
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    insertText: (text: string) => {
      if (editorRef.current) {
        // Get the current editor instance
        const editor = editorRef.current;
        
        // Insert text at cursor position
        editor.focus();
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          range.insertNode(document.createTextNode(text));
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          // If no selection, append to the end
          editor.innerHTML += text;
        }
        
        // Trigger onChange to update the value
        const newHtml = editor.innerHTML;
        onChange(newHtml);
      }
    },
    focus: () => {
      if (editorRef.current) {
        editorRef.current.focus();
      }
    }
  }));

  return (
    <div className={`border border-gray-300 rounded-md overflow-hidden ${className}`}>
      <div className={`flex items-center gap-1 p-2 border-b bg-gray-50 ${editorDir === 'rtl' ? "flex-row-reverse" : ""}`}>
        <button
          type="button"
          onClick={() => formatDoc('bold')}
          className="p-1.5 rounded hover:bg-gray-200"
          title="Bold"
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          onClick={() => formatDoc('italic')}
          className="p-1.5 rounded hover:bg-gray-200"
          title="Italic"
        >
          <Italic size={16} />
        </button>
        <button
          type="button"
          onClick={() => formatDoc('insertUnorderedList')}
          className="p-1.5 rounded hover:bg-gray-200"
          title="Bullet List"
        >
          <List size={16} />
        </button>
        <div className="h-6 w-px bg-gray-300 mx-1"></div>
        
        <button
          type="button"
          onClick={() => formatDoc('justifyLeft')}
          className="p-1.5 rounded hover:bg-gray-200"
          title="Align Left"
        >
          <AlignLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => formatDoc('justifyCenter')}
          className="p-1.5 rounded hover:bg-gray-200"
          title="Align Center"
        >
          <AlignCenter size={16} />
        </button>
        <button
          type="button"
          onClick={() => formatDoc('justifyRight')}
          className="p-1.5 rounded hover:bg-gray-200"
          title="Align Right"
        >
          <AlignRight size={16} />
        </button>
      </div>
      
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        onFocus={handleFocus}
        onBlur={handleBlur}
        dir={isHebrew ? 'rtl' : 'ltr'}
        style={{ 
          minHeight, 
          outline: 'none',
          textAlign: isHebrew ? 'right' : 'left',
          unicodeBidi: 'plaintext' // This is crucial for mixed content
        }}
        className={`p-3 outline-none ${required ? 'required-editor' : ''} empty:before:text-gray-400 empty:before:content-[attr(data-placeholder)]`}
        data-placeholder={placeholder}
        aria-required={required}
      />
    </div>
  );
});

export default RichTextEditor;
